import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AccessibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccessibilityModal = ({ isOpen, onClose }: AccessibilityModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-display font-bold">Accessibility Statement</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6 text-sm font-body">
            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Our Commitment</h3>
              <p className="text-muted-foreground leading-[1.65]">
                Sink Bike Run is committed to ensuring digital accessibility for people with 
                disabilities. We are continually improving the user experience for everyone and 
                applying the relevant accessibility standards to make our triathlon preparation 
                tools accessible to all athletes.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Accessibility Features</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                Our triathlon checklist application includes the following accessibility features:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Keyboard navigation support for all interactive elements including checklists</li>
                <li>Screen reader compatibility with proper ARIA labels and live regions</li>
                <li>High contrast mode support and dark/light theme options</li>
                <li>Descriptive alt text for all images, including triathlon-related graphics</li>
                <li>Clear heading structure and semantic HTML markup</li>
                <li>Focus indicators for interactive elements (buttons, form fields, checkboxes)</li>
                <li>Consistent navigation structure across all pages</li>
                <li>Resizable text up to 200% without loss of functionality</li>
                <li>Skip links to main content areas</li>
                <li>Accessible drag-and-drop functionality with keyboard alternatives</li>
                <li>Form validation with clear error messaging</li>
                <li>Modal dialogs with proper focus management and escape key support</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Standards Compliance</h3>
              <p className="text-muted-foreground leading-[1.65]">
                We have made efforts to comply with the Web Content Accessibility Guidelines (WCAG) 2.1 
                Level AA standards and the Americans with Disabilities Act (ADA). However, we cannot 
                guarantee full accessibility compliance at all times. We acknowledge that some areas 
                of our website may not be fully accessible to all users.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Assistive Technologies</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                Our website is designed to be compatible with assistive technologies such as:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Screen readers (NVDA, JAWS, VoiceOver)</li>
                <li>Voice recognition software</li>
                <li>Keyboard-only navigation</li>
                <li>Screen magnification software</li>
                <li>High contrast and dark mode displays</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Accessibility Disclaimer</h3>
              <p className="text-muted-foreground leading-[1.65]">
                While we strive to maintain accessibility standards, we cannot guarantee that our 
                website will be accessible to all users at all times. We are aware of some 
                accessibility limitations and are working to address them as resources permit. 
                If you encounter barriers while using our service, please report them to us.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Feedback and Contact</h3>
              <p className="text-muted-foreground leading-[1.65] mb-2">
                We welcome your feedback on the accessibility of Sink Bike Run. Please let us 
                know if you encounter accessibility barriers:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Use our contact form</li>
                <li>Email: info@sinkbikerun.com</li>
                <li>Describe the specific issue and the page where you encountered it</li>
                <li>Include your contact information if you'd like a response</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Continuous Improvement</h3>
              <p className="text-muted-foreground leading-[1.65]">
                We are committed to ongoing accessibility improvements. Our team regularly 
                reviews and updates our website to enhance accessibility and usability for 
                all users. We also conduct periodic accessibility audits and user testing 
                with assistive technologies.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-display font-semibold mb-2">Third-Party Content</h3>
              <p className="text-muted-foreground leading-[1.65]">
                While we strive to ensure accessibility across our entire website, some 
                third-party content may not be fully accessible. We work with our partners 
                to improve accessibility where possible.
              </p>
              <p className="text-foreground font-body leading-[1.7] mt-2">
                <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AccessibilityModal;